// WeeklyActivitiesTab.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Select,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ViewDayIcon from "@mui/icons-material/ViewDay";
import dayjs from "dayjs";
import { SERVER_URL } from "../../constants";
import { fetchJson } from "./utils/http";

function hasWeekday(startDate, endDate) {
  if (!startDate || !endDate) return false;
  let d = dayjs(startDate);
  const end = dayjs(endDate);
  while (d.isBefore(end) || d.isSame(end, "day")) {
    const dow = d.day(); // 0=Sun, 6=Sat
    if (dow >= 1 && dow <= 5) return true;
    d = d.add(1, "day");
  }
  return false;
}

function getResidentialWeeks(bookings = []) {
  return bookings
    .filter((b) => String(b.productType) === "0" && hasWeekday(b.startDate, b.endDate))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
}

// Normalise date fields from either camelCase or snake_case
function getRange(obj) {
  if (!obj) return { start: null, end: null };
  const start = obj.startDate || obj.start_date || null;
  const end = obj.endDate || obj.end_date || null;
  return { start, end };
}

function getAllWeekColumns(bookingsByStudent) {
  const weekSet = new Set();

  Object.values(bookingsByStudent).forEach((bookings) => {
    getResidentialWeeks(bookings).forEach((res) => {
      weekSet.add(res.startDate);
    });
  });

  const starts = Array.from(weekSet);
  starts.sort();

  return starts.map((startDate, idx) => {
    // Find a sample residential booking to get endDate
    let sample = null;
    outer: for (const bookings of Object.values(bookingsByStudent)) {
      for (const b of bookings) {
        if (String(b.productType) === "0" && b.startDate === startDate) {
          sample = b;
          break outer;
        }
      }
    }

    const start = dayjs(startDate);
    const end = sample ? dayjs(sample.endDate) : start.add(7, "day");

    return {
      key: startDate,
      index: idx,
      startDate,
      endDate: sample?.endDate || end.format("YYYY-MM-DD"),
      label: `Week ${idx + 1} (${start.format("DD MMM")} – ${end.format(
        "DD MMM"
      )})`,
    };
  });
}

// Activity (booking or product) overlaps this residential week
function activityBelongsToWeek(activity, week) {
  const { start, end } = getRange(activity);
  if (!start || !end) return false;

  // Treat both ranges as [start, end] and include any overlap.
  // Dates are "YYYY-MM-DD" so string comparison is safe.
  return !(end < week.startDate || start > week.endDate);
}

// Weekly activity booking types: AFTERNOON + EXTERNAL (and numeric 1/3 for compatibility)
function isWeeklyActivityBooking(b) {
  if (!b) return false;
  const t = b.productType;

  if (t == null) return false;

  // numeric codes (if any)
  const n = Number(t);
  if (!Number.isNaN(n) && (n === 1 || n === 3)) return true;

  const s = String(t).toUpperCase();
  return s === "AFTERNOON" || s === "EXTERNAL";
}

// Single-day product: startDate === endDate
function isDailyProduct(p) {
  const { start, end } = getRange(p);
  return start && end && start === end;
}

// Generate Mon–Fri date strings for a residential week
function getWeekdays(weekStartDate, weekEndDate) {
  const days = [];
  let d = dayjs(weekStartDate);
  const end = dayjs(weekEndDate);
  while (d.isBefore(end) || d.isSame(end, "day")) {
    const dow = d.day();
    if (dow >= 1 && dow <= 5) {
      days.push({
        date: d.format("YYYY-MM-DD"),
        label: d.format("ddd DD MMM"), // e.g. "Mon 06 Jul"
        shortLabel: d.format("ddd"),    // e.g. "Mon"
      });
    }
    d = d.add(1, "day");
  }
  return days;
}

// Find activity bookings for a specific single day
function findActivitiesForDay(bookings, dateStr) {
  if (!bookings) return [];
  return bookings.filter((b) => {
    if (!isWeeklyActivityBooking(b)) return false;
    const { start, end } = getRange(b);
    return start === dateStr && end === dateStr;
  });
}

function findActivityForDay(bookings, dateStr) {
  const acts = findActivitiesForDay(bookings, dateStr);
  if (!acts.length) return null;
  return acts.reduce((best, cur) =>
    Number(cur.bookingId) > Number(best.bookingId) ? cur : best
  );
}

function findActivitiesForWeek(bookings, week) {
  if (!bookings) return [];
  return bookings.filter(
    (b) => isWeeklyActivityBooking(b) && activityBelongsToWeek(b, week)
  );
}

function findActivityForWeek(bookings, week) {
  const acts = findActivitiesForWeek(bookings, week);
  if (!acts.length) return null;

  // pick newest / highest bookingId so UI is stable
  return acts.reduce((best, cur) =>
    Number(cur.bookingId) > Number(best.bookingId) ? cur : best
  );
}

async function applyChange(change, token) {
  const { studentId, oldBookings = [], newProductId, newProductDefaultCharge } = change;

  console.log("🧪 applyChange called with:", {
    studentId,
    oldBookings,
    newProductId,
  });

  // 1) delete ALL old bookings (if any)
  if (Array.isArray(oldBookings) && oldBookings.length > 0) {
    for (const ob of oldBookings) {
      if (!ob?.bookingId) continue;

      const url = `${SERVER_URL}api/bookings/${ob.bookingId}/product/${ob.productId}`;
      console.log("🧪 applyChange: deleting booking", { url });

      try {
        await fetchJson(url, token, { method: "DELETE" });
      } catch (err) {
        const msg = String(err?.message || err);
        const status = err?.status;
        if (status === 404 || msg.includes(" 404 ")) {
          console.warn("⚠️ delete returned 404 (already deleted) – ignoring", { ob });
          continue;
        }
        console.error("💥 delete error:", { ob, err });
        throw err;
      }
    }
  }

  // 2) if user selected "None" (null), we're done after deletion
  if (newProductId == null) return;

  // 3) create the new booking
  const payload = {
  bookingStatus: "PROVISIONAL",
  ...(newProductDefaultCharge != null ? { actualCharge: newProductDefaultCharge } : {}),
};
  const url = `${SERVER_URL}api/bookings/studentId/${studentId}/productId/${newProductId}`;
  console.log("🧪 applyChange: creating new booking", { url, payload });

  await fetchJson(url, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

async function fetchWeeklyActivitiesContext(studentIds, token) {
  return fetchJson(`${SERVER_URL}api/weekly-activities/context`, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ studentIds }),
  });
}

/**
 * WeeklyActivitiesTab
 *
 * Props:
 * - students: [{ id, firstName, surname, fullName? }]
 * - showToast?: (msg: string, severity?: "info"|"success"|"warning"|"error") => void
 */
export default function WeeklyActivitiesTab({ students = [], showToast }) {
  const [bookingsByStudent, setBookingsByStudent] = useState({});
  const [eligibleByStudent, setEligibleByStudent] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({});
  const [expandedWeeks, setExpandedWeeks] = useState(new Set()); // week keys in daily mode
  const [viewSwitchConfirm, setViewSwitchConfirm] = useState(null); // { weekKey, direction, bookingIds, count }

  // NEW: resolved student name cache
  const [resolvedNames, setResolvedNames] = useState({}); // { [studentId]: { fullName, firstName, surname } }

  const token =
    typeof window !== "undefined"
      ? sessionStorage.getItem("bearer")
      : null;

  const studentIds = useMemo(
    () => students.map((s) => s.id).filter(Boolean),
    [students]
  );

  //fetch student names
  useEffect(() => {
    if (!token || !studentIds.length) return;

    const missingIds = students
      .filter((s) => {
        const hasName =
          s.fullName ||
          s.studentName ||
          s.studentSurname ||
          s.firstName ||
          s.surname;
        return !hasName && s.id && !resolvedNames[s.id];
      })
      .map((s) => s.id);

    if (!missingIds.length) return;

    let cancelled = false;

    (async () => {
      try {
        // ✅ one call for all missing ids
        const unique = Array.from(new Set(missingIds));

        const res = await fetch(`${SERVER_URL}api/students/batch`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ ids: unique }),
        });

        if (!res.ok) throw new Error(`students/batch -> ${res.status}`);

        const data = await res.json(); // array of Student
        const byId = new Map((data || []).map((s) => [s.id, s]));

        const entries = unique.map((id) => {
          const s = byId.get(id);

          const firstName =
            s?.firstName || s?.studentName || s?.name || "";
          const surname =
            s?.surname || s?.studentSurname || s?.lastName || "";
          const fullName =
            s?.fullName ||
            [firstName, surname].filter(Boolean).join(" ") ||
            null;

          return [id, { fullName, firstName, surname }];
        });

        if (!cancelled) {
          setResolvedNames((prev) => {
            const next = { ...prev };
            for (const [id, value] of entries) next[id] = value;
            return next;
          });
        }
      } catch (e) {
        console.error("Failed to resolve student names (batch)", e);
        if (!cancelled) {
          // fallback: mark as unresolved so we don't retry endlessly
          setResolvedNames((prev) => {
            const next = { ...prev };
            for (const id of missingIds) {
              next[id] = next[id] || { fullName: null };
            }
            return next;
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // keep deps stable: don’t stringify huge objects if you can avoid it
  }, [token, students, studentIds, resolvedNames]);

  useEffect(() => {
    if (!studentIds.length || !token) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const ctx = await fetchWeeklyActivitiesContext(studentIds, token);

        if (!cancelled) {
          setBookingsByStudent(ctx.bookingsByStudent || {});
          setEligibleByStudent(ctx.eligibleWeeklyByStudent || {});
          setDraft({});
        }
      } catch (e) {
        console.error("Failed to load weekly activities context", e);
        if (!cancelled) setError("Failed to load data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token, studentIds.join(",")]);

  const weekColumns = useMemo(
    () => getAllWeekColumns(bookingsByStudent),
    [bookingsByStudent]
  );

  // Auto-expand weeks that already have single-day activity bookings
  useEffect(() => {
    if (!weekColumns.length) return;
    const autoExpand = new Set();
    for (const week of weekColumns) {
      const days = getWeekdays(week.startDate, week.endDate);
      for (const s of students) {
        const bookings = bookingsByStudent[s.id] || [];
        for (const day of days) {
          if (findActivitiesForDay(bookings, day.date).length > 0) {
            autoExpand.add(week.key);
            break;
          }
        }
        if (autoExpand.has(week.key)) break;
      }
    }
    if (autoExpand.size > 0) {
      setExpandedWeeks((prev) => {
        const next = new Set(prev);
        for (const k of autoExpand) next.add(k);
        return next;
      });
    }
  }, [weekColumns, bookingsByStudent, students]);

  function draftKey(studentId, weekStart) {
    return `${studentId}|${weekStart}`;
  }

  function getCurrentSelection(studentId, week) {
    const key = draftKey(studentId, week.startDate);
    if (Object.prototype.hasOwnProperty.call(draft, key)) {
      // draft can be null => "None selected"
      return draft[key] ?? "";
    }
    const existing = findActivityForWeek(
      bookingsByStudent[studentId],
      week
    );
    return existing ? existing.productId : "";
  }

  function handleChange(studentId, week, value) {
    const key = draftKey(studentId, week.startDate);
    setDraft((prev) => ({
      ...prev,
      [key]: value === "" ? null : value,
    }));
  }

  // Build union of eligible activities per week (for "set all" dropdown)
  const optionsByWeek = useMemo(() => {
    const map = {};
    for (const week of weekColumns) {
      const seen = new Map(); // productId -> { productId, productName }
      for (const s of students) {
        const bookings = bookingsByStudent[s.id] || [];
        const residentialWeeks = getResidentialWeeks(bookings);
        const hasWeek = residentialWeeks.some((w) => w.startDate === week.startDate);
        if (!hasWeek) continue;

        const eligible = eligibleByStudent[s.id] || [];
        for (const p of eligible) {
          if (activityBelongsToWeek(p, week) && !seen.has(p.productId)) {
            seen.set(p.productId, { productId: p.productId, productName: p.productName });
          }
        }
      }
      map[week.key] = Array.from(seen.values());
    }
    return map;
  }, [weekColumns, students, bookingsByStudent, eligibleByStudent]);

  function handleSetAll(week, value) {
    const productId = value === "" ? null : value;
    setDraft((prev) => {
      const next = { ...prev };
      for (const s of students) {
        const bookings = bookingsByStudent[s.id] || [];
        const residentialWeeks = getResidentialWeeks(bookings);
        const hasWeek = residentialWeeks.some((w) => w.startDate === week.startDate);
        if (!hasWeek) continue;
        next[draftKey(s.id, week.startDate)] = productId;
      }
      return next;
    });
  }

  // --- Daily view helpers ---

  function toggleDailyView(weekKey) {
    const week = weekColumns.find((w) => w.key === weekKey);
    if (!week) return;

    const isCurrentlyDaily = expandedWeeks.has(weekKey);
    const direction = isCurrentlyDaily ? "toWeekly" : "toDaily";

    // Collect bookings that would be cancelled by the switch
    const conflictIds = [];
    for (const s of students) {
      const bookings = bookingsByStudent[s.id] || [];
      const residentialWeeks = getResidentialWeeks(bookings);
      if (!residentialWeeks.some((w) => w.startDate === week.startDate)) continue;

      if (direction === "toDaily") {
        // Switching to daily: find weekly (multi-day) activity bookings in this week
        const weeklyActs = findActivitiesForWeek(bookings, week).filter((b) => {
          const { start, end } = getRange(b);
          return start !== end; // multi-day = weekly product
        });
        for (const b of weeklyActs) if (b.bookingId) conflictIds.push(b.bookingId);
      } else {
        // Switching to weekly: find single-day activity bookings in this week
        const days = getWeekdays(week.startDate, week.endDate);
        for (const day of days) {
          const dailyActs = findActivitiesForDay(bookings, day.date);
          for (const b of dailyActs) if (b.bookingId) conflictIds.push(b.bookingId);
        }
      }
    }

    if (conflictIds.length > 0) {
      setViewSwitchConfirm({ weekKey, direction, bookingIds: conflictIds, count: conflictIds.length });
    } else {
      // No conflicts, just toggle
      setExpandedWeeks((prev) => {
        const next = new Set(prev);
        if (next.has(weekKey)) next.delete(weekKey);
        else next.add(weekKey);
        return next;
      });
    }
  }

  async function confirmViewSwitch() {
    if (!viewSwitchConfirm) return;
    const { weekKey, bookingIds } = viewSwitchConfirm;

    try {
      setSaving(true);

      // Batch-delete the conflicting bookings
      await fetchJson(`${SERVER_URL}api/bookings/batch-delete`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: bookingIds }),
      });

      // Toggle the view
      setExpandedWeeks((prev) => {
        const next = new Set(prev);
        if (next.has(weekKey)) next.delete(weekKey);
        else next.add(weekKey);
        return next;
      });

      // Clear any draft entries for this week's students (stale after delete)
      const week = weekColumns.find((w) => w.key === weekKey);
      if (week) {
        setDraft((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(next)) {
            // Remove weekly draft keys for this week
            if (key.endsWith(`|${week.startDate}`)) delete next[key];
            // Remove daily draft keys for days in this week
            if (key.includes("|day|")) {
              const dateStr = key.split("|day|")[1];
              const days = getWeekdays(week.startDate, week.endDate);
              if (days.some((d) => d.date === dateStr)) delete next[key];
            }
          }
          return next;
        });
      }

      // Reload to reflect deletions
      const ctx = await fetchWeeklyActivitiesContext(studentIds, token);
      setBookingsByStudent(ctx.bookingsByStudent || {});
      setEligibleByStudent(ctx.eligibleWeeklyByStudent || {});

      showToast?.(`${bookingIds.length} booking${bookingIds.length === 1 ? "" : "s"} cancelled.`, "info");
    } catch (e) {
      console.error("Failed to cancel bookings on view switch", e);
      showToast?.("Failed to cancel bookings (see console).", "error");
    } finally {
      setSaving(false);
      setViewSwitchConfirm(null);
    }
  }

  function dailyDraftKey(studentId, dateStr) {
    return `${studentId}|day|${dateStr}`;
  }

  function getCurrentDailySelection(studentId, dateStr) {
    const key = dailyDraftKey(studentId, dateStr);
    if (Object.prototype.hasOwnProperty.call(draft, key)) {
      return draft[key] ?? "";
    }
    const existing = findActivityForDay(bookingsByStudent[studentId], dateStr);
    return existing ? existing.productId : "";
  }

  function handleDailyChange(studentId, dateStr, value) {
    const key = dailyDraftKey(studentId, dateStr);
    setDraft((prev) => ({
      ...prev,
      [key]: value === "" ? null : value,
    }));
  }

  // Build union of eligible daily products per day (for "set all" in daily mode)
  const dailyOptionsByDate = useMemo(() => {
    const map = {};
    for (const week of weekColumns) {
      if (!expandedWeeks.has(week.key)) continue;
      const days = getWeekdays(week.startDate, week.endDate);
      for (const day of days) {
        const seen = new Map();
        for (const s of students) {
          const bookings = bookingsByStudent[s.id] || [];
          const residentialWeeks = getResidentialWeeks(bookings);
          if (!residentialWeeks.some((w) => w.startDate === week.startDate)) continue;

          const eligible = eligibleByStudent[s.id] || [];
          for (const p of eligible) {
            if (!isDailyProduct(p)) continue;
            const { start } = getRange(p);
            if (start === day.date && !seen.has(p.productId)) {
              seen.set(p.productId, {
                productId: p.productId,
                productName: p.productName,
                defaultCharge: p.defaultCharge,
              });
            }
          }
        }
        map[day.date] = Array.from(seen.values());
      }
    }
    return map;
  }, [weekColumns, expandedWeeks, students, bookingsByStudent, eligibleByStudent]);

  function handleSetAllDaily(dateStr, value) {
    const productId = value === "" ? null : value;
    // find which week this date belongs to
    const week = weekColumns.find((w) => {
      const days = getWeekdays(w.startDate, w.endDate);
      return days.some((d) => d.date === dateStr);
    });
    if (!week) return;

    setDraft((prev) => {
      const next = { ...prev };
      for (const s of students) {
        const bookings = bookingsByStudent[s.id] || [];
        const residentialWeeks = getResidentialWeeks(bookings);
        if (!residentialWeeks.some((w) => w.startDate === week.startDate)) continue;
        next[dailyDraftKey(s.id, dateStr)] = productId;
      }
      return next;
    });
  }

  async function handleSave() {
    if (!token) {
      showToast?.("Missing auth token; cannot save.", "error");
      return;
    }

    const changes = [];

    studentIds.forEach((studentId) => {
      const bookings = bookingsByStudent[studentId] || [];

      weekColumns.forEach((week) => {
        const key = draftKey(studentId, week.startDate);
        if (!Object.prototype.hasOwnProperty.call(draft, key)) return;

        const newProductId = draft[key]; // can be null

        const existingBookings = findActivitiesForWeek(bookings, week);
        const oldBookings = existingBookings.map((b) => ({
          bookingId: b.bookingId,
          productId: b.productId,
        }));

        // "No change" if:
        // - new is null and there were no existing bookings
        // - OR new matches ANY existing booking productId AND there's only one booking (optional),
        //   but safest is: if new matches one of the existing productIds AND you don't care about duplicates.
        const existingProductIds = new Set(oldBookings.map((b) => Number(b.productId)));

        const newPid = newProductId == null ? null : Number(newProductId);

        const noChange =
          (newPid == null && oldBookings.length === 0) ||
          (newPid != null && existingProductIds.has(newPid) && oldBookings.length === 1);

        if (noChange) return;

        const selectedOpt =
  newPid == null
    ? null
    : (eligibleByStudent[studentId] || []).find((p) => Number(p.productId) === newPid);

changes.push({
  studentId,
  weekStartDate: week.startDate,
  weekEndDate: week.endDate,
  oldBookings,
  newProductId: newPid,
  newProductDefaultCharge:
    selectedOpt?.defaultCharge != null ? Number(selectedOpt.defaultCharge) : null,
});

      });
    });

    // Collect daily draft changes (keys like "studentId|day|YYYY-MM-DD")
    for (const [key, newProductId] of Object.entries(draft)) {
      const parts = key.split("|");
      if (parts[1] !== "day") continue; // skip weekly keys
      const studentId = Number(parts[0]);
      const dateStr = parts[2];
      if (!studentId || !dateStr) continue;

      const bookings = bookingsByStudent[studentId] || [];
      const existingBookings = findActivitiesForDay(bookings, dateStr);
      const oldBookings = existingBookings.map((b) => ({
        bookingId: b.bookingId,
        productId: b.productId,
      }));

      const existingProductIds = new Set(oldBookings.map((b) => Number(b.productId)));
      const newPid = newProductId == null ? null : Number(newProductId);

      const noChange =
        (newPid == null && oldBookings.length === 0) ||
        (newPid != null && existingProductIds.has(newPid) && oldBookings.length === 1);
      if (noChange) continue;

      const selectedOpt =
        newPid == null
          ? null
          : (eligibleByStudent[studentId] || []).find((p) => Number(p.productId) === newPid);

      changes.push({
        studentId,
        weekStartDate: dateStr,
        weekEndDate: dateStr,
        oldBookings,
        newProductId: newPid,
        newProductDefaultCharge:
          selectedOpt?.defaultCharge != null ? Number(selectedOpt.defaultCharge) : null,
      });
    }

    console.log("🧪 WeeklyActivities: built changes array:", changes);

    if (!changes.length) {
      showToast?.("No changes to save.", "info");
      return;
    }

    try {
      setSaving(true);

      // Collect all deletions and creations into batch arrays
      const deleteIds = [];
      const createBookings = [];

      for (const change of changes) {
        const { studentId, oldBookings = [], newProductId, newProductDefaultCharge } = change;

        // gather booking IDs to delete
        for (const ob of oldBookings) {
          if (ob?.bookingId) deleteIds.push(ob.bookingId);
        }

        // gather bookings to create (skip if user selected "None")
        if (newProductId != null) {
          createBookings.push({
            studentId,
            productId: newProductId,
            bookingStatus: "PROVISIONAL",
            ...(newProductDefaultCharge != null ? { actualCharge: newProductDefaultCharge } : {}),
          });
        }
      }

      console.log("🧪 WeeklyActivities: batch save", {
        deleteCount: deleteIds.length,
        createCount: createBookings.length,
      });

      // Fire batch delete then batch create
      if (deleteIds.length) {
        await fetchJson(`${SERVER_URL}api/bookings/batch-delete`, token, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: deleteIds }),
        });
      }

      if (createBookings.length) {
        await fetchJson(`${SERVER_URL}api/bookings/batch-create`, token, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bookings: createBookings }),
        });
      }

      showToast?.("Weekly activities updated.", "success");

      // Reload context to reflect changes (single call)
      console.log("🧪 WeeklyActivities: reloading weekly activities context");
      const ctx = await fetchWeeklyActivitiesContext(studentIds, token);
      console.log("🧪 WeeklyActivities: reload result:", ctx);

      setBookingsByStudent(ctx.bookingsByStudent || {});
      setEligibleByStudent(ctx.eligibleWeeklyByStudent || {});
      setDraft({});

    } catch (e) {
      console.error("💥 WeeklyActivities: unexpected failure in handleSave", e);
      showToast?.("Error saving weekly activities (see console).", "error");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!students.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2">
          No students added yet.
          <br />
          <br />
          Add students to the group to manage weekly activities.
        </Typography>
      </Box>
    );
  }

  if (!weekColumns.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2">
          No residential weeks found for these students.
          <br />
          <br />
          Have you aligned bookings? If you do this on the first Details tab,
          residential weeks will be auto-booked to match group stay settings.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          mb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="h6">Weekly activities</Typography>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              {weekColumns.map((week) => {
                const isExpanded = expandedWeeks.has(week.key);
                const days = isExpanded ? getWeekdays(week.startDate, week.endDate) : [];

                if (isExpanded) {
                  // Render a header cell per weekday
                  return days.map((day, di) => (
                    <TableCell key={`${week.key}|${day.date}`} align="center" sx={{ minWidth: 160 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        {di === 0 && (
                          <Tooltip title="Switch to weekly view">
                            <IconButton size="small" onClick={() => toggleDailyView(week.key)}>
                              <CalendarViewWeekIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {day.label}
                        </Typography>
                      </Box>
                      <Select
                        size="small"
                        value=""
                        displayEmpty
                        onChange={(e) => handleSetAllDaily(day.date, e.target.value)}
                        sx={{ minWidth: 140, mt: 0.5, display: "block" }}
                        renderValue={() => <em style={{ color: "#888" }}>Set all…</em>}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {(dailyOptionsByDate[day.date] || []).map((opt) => (
                          <MenuItem key={opt.productId} value={opt.productId}>
                            {opt.productName}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  ));
                }

                // Weekly (collapsed) header
                return (
                  <TableCell key={week.key} align="center">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      {week.label}
                      <Tooltip title="Switch to daily view">
                        <IconButton size="small" onClick={() => toggleDailyView(week.key)}>
                          <ViewDayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Select
                      size="small"
                      value=""
                      displayEmpty
                      onChange={(e) => handleSetAll(week, e.target.value)}
                      sx={{ minWidth: 160, mt: 0.5, display: "block" }}
                      renderValue={() => <em style={{ color: "#888" }}>Set all…</em>}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {(optionsByWeek[week.key] || []).map((opt) => (
                        <MenuItem key={opt.productId} value={opt.productId}>
                          {opt.productName}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map((s) => {
              const bookings = bookingsByStudent[s.id] || [];
              const residentialWeeks = getResidentialWeeks(bookings);
              const hasWeek = new Set(
                residentialWeeks.map((w) => w.startDate)
              );
              const eligible = eligibleByStudent[s.id] || [];
              const resolved = resolvedNames[s.id] || {};
              const name =
                s.fullName ||
                [s.studentName, s.studentSurname].filter(Boolean).join(" ") ||
                [s.firstName, s.surname].filter(Boolean).join(" ") ||
                resolved.fullName ||
                [resolved.firstName, resolved.surname].filter(Boolean).join(" ") ||
                `Student ${s.id}`;

              return (
                <TableRow key={s.id}>
                  <TableCell>{name}</TableCell>
                  {weekColumns.map((week) => {
                    const isExpanded = expandedWeeks.has(week.key);
                    const days = isExpanded ? getWeekdays(week.startDate, week.endDate) : [];

                    if (!hasWeek.has(week.startDate)) {
                      // No residential booking for this week — show dash(es)
                      if (isExpanded) {
                        return days.map((day) => (
                          <TableCell key={`${week.key}|${day.date}|${s.id}`} align="center">—</TableCell>
                        ));
                      }
                      return (
                        <TableCell key={week.key} align="center">—</TableCell>
                      );
                    }

                    if (isExpanded) {
                      // Render a cell per weekday with daily product dropdowns
                      return days.map((day) => {
                        const dayOptions = (eligible || []).filter((p) => {
                          if (!isDailyProduct(p)) return false;
                          const { start } = getRange(p);
                          return start === day.date;
                        });

                        const current = getCurrentDailySelection(s.id, day.date);

                        // Ensure existing booking shows in options
                        const existingDay = findActivityForDay(bookings, day.date);
                        if (
                          existingDay &&
                          !dayOptions.some((o) => o.productId === existingDay.productId)
                        ) {
                          dayOptions.push({
                            productId: existingDay.productId,
                            productName: existingDay.productName,
                            startDate: existingDay.startDate,
                            endDate: existingDay.endDate,
                          });
                        }

                        return (
                          <TableCell key={`${week.key}|${day.date}|${s.id}`} align="center">
                            <Select
                              size="small"
                              value={current}
                              displayEmpty
                              onChange={(e) => handleDailyChange(s.id, day.date, e.target.value)}
                              sx={{ minWidth: 140 }}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {dayOptions.map((opt) => (
                                <MenuItem key={opt.productId} value={opt.productId}>
                                  {opt.productName}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                        );
                      });
                    }

                    // Weekly (collapsed) — single select per week
                    const options = (eligible || []).filter((p) =>
                      activityBelongsToWeek(p, week)
                    );

                    const current = getCurrentSelection(s.id, week);

                    const existing = findActivityForWeek(bookings, week);
                    if (
                      existing &&
                      !options.some((o) => o.productId === existing.productId)
                    ) {
                      options.push({
                        productId: existing.productId,
                        productName: existing.productName,
                        startDate: existing.startDate,
                        endDate: existing.endDate,
                      });
                    }

                    return (
                      <TableCell key={week.key} align="center">
                        <Select
                          size="small"
                          value={current}
                          displayEmpty
                          onChange={(e) =>
                            handleChange(s.id, week, e.target.value)
                          }
                          sx={{ minWidth: 160 }}
                        >
                          <MenuItem value="">
                            <em>None selected</em>
                          </MenuItem>
                          {options.map((opt) => (
                            <MenuItem
                              key={opt.productId}
                              value={opt.productId}
                            >
                              {opt.productName}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirm view-switch dialog */}
      <Dialog
        open={!!viewSwitchConfirm}
        onClose={() => setViewSwitchConfirm(null)}
      >
        <DialogTitle>
          Switch to {viewSwitchConfirm?.direction === "toDaily" ? "daily" : "weekly"} view?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {viewSwitchConfirm?.direction === "toDaily"
              ? `This week has ${viewSwitchConfirm?.count} weekly activity booking${viewSwitchConfirm?.count === 1 ? "" : "s"} that will be cancelled before switching to daily view.`
              : `This week has ${viewSwitchConfirm?.count} daily activity booking${viewSwitchConfirm?.count === 1 ? "" : "s"} that will be cancelled before switching to weekly view.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSwitchConfirm(null)}>Cancel</Button>
          <Button
            onClick={confirmViewSwitch}
            variant="contained"
            color="warning"
            disabled={saving}
          >
            {saving ? "Cancelling…" : `Cancel ${viewSwitchConfirm?.count === 1 ? "booking" : "bookings"} & switch`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
